import {
  DAY_LENGTH,
  MAX_ORDERS,
  MOISTURE_DURATION,
  ORDER_INTERVAL,
  RAIN_CHANCE_PER_DAY,
  RAINBOW_DURATION,
} from './constants';
import type { GameEvent } from './events';
import { FLOWERS } from './flowers';
import { rollMarket } from './market';
import { generateOrder } from './orders';
import { Rng } from './rng';
import type { GameState } from './state';
import { goldenChance, growthMultiplier, sprinklerInterval } from './upgrades';

export interface TickOptions {
  /**
   * Only the authority (solo player or multiplayer host) rolls random events
   * such as golden blooms, rain and new orders. Visitors run a non-authoritative
   * tick purely to keep clocks and growth bars smooth between host snapshots.
   */
  authoritative: boolean;
}

/** Longest single step used when fast-forwarding (offline progress, sleeping). */
const MAX_STEP = 2;

export function waterAllPlots(state: GameState): void {
  for (const plot of state.plots) if (plot.unlocked) plot.moisture = 1;
}

function startNewDay(state: GameState, rng: Rng, events: GameEvent[]): void {
  state.market = rollMarket(state, rng);
  state.weather.nextRainAt = rng.chance(RAIN_CHANCE_PER_DAY)
    ? state.time + rng.range(0.1, 0.75) * DAY_LENGTH
    : null;
  events.push({ type: 'newDay', day: state.day, featured: state.market.featured });
}

function updateWeather(
  state: GameState,
  dt: number,
  rng: Rng,
  opts: TickOptions,
  events: GameEvent[],
): void {
  const weather = state.weather;
  weather.rainbowLeft = Math.max(0, weather.rainbowLeft - dt);

  if (weather.raining) {
    waterAllPlots(state);
    weather.timeLeft -= dt;
    if (weather.timeLeft <= 0 && opts.authoritative) {
      weather.raining = false;
      weather.timeLeft = 0;
      weather.rainbowLeft = RAINBOW_DURATION;
      events.push({ type: 'rainStop' });
    }
    return;
  }

  if (opts.authoritative && weather.nextRainAt !== null && state.time >= weather.nextRainAt) {
    weather.raining = true;
    weather.timeLeft = rng.range(45, 100);
    weather.nextRainAt = null;
    waterAllPlots(state);
    events.push({ type: 'rainStart' });
  }
}

/** Advances the simulation by `dt` seconds and returns what happened. */
export function tick(state: GameState, dt: number, opts: TickOptions): GameEvent[] {
  const events: GameEvent[] = [];
  if (!(dt > 0)) return events;
  const rng = new Rng(state);

  state.time += dt;
  state.dayTime += dt / DAY_LENGTH;
  while (state.dayTime >= 1) {
    state.dayTime -= 1;
    state.day += 1;
    if (opts.authoritative) startNewDay(state, rng, events);
  }

  updateWeather(state, dt, rng, opts, events);

  const sprinklerLevel = state.upgrades.sprinkler;
  if (sprinklerLevel > 0) {
    state.sprinklerTimer -= dt;
    if (state.sprinklerTimer <= 0) {
      state.sprinklerTimer = sprinklerInterval(sprinklerLevel);
      waterAllPlots(state);
      // Visitors mirror the watering but leave the event to the host's broadcast.
      if (opts.authoritative) events.push({ type: 'sprinklers' });
    }
  }

  const speed = growthMultiplier(state.upgrades.compost);
  for (const plot of state.plots) {
    if (!plot.unlocked) continue;
    // Flowers only grow for the part of this step during which the soil was wet.
    const wetTime = Math.min(dt, plot.moisture * MOISTURE_DURATION);
    if (!state.weather.raining) {
      plot.moisture = Math.max(0, plot.moisture - dt / MOISTURE_DURATION);
    }

    const crop = plot.crop;
    if (!crop || crop.growth >= 1 || wetTime <= 0) continue;
    crop.growth = Math.min(1, crop.growth + (wetTime * speed) / FLOWERS[crop.species].growTime);
    if (crop.growth >= 1 && opts.authoritative) {
      crop.golden = rng.chance(goldenChance(state.upgrades.beehive));
      events.push({ type: 'bloomed', plotId: plot.id, species: crop.species, golden: crop.golden });
    }
  }

  if (opts.authoritative) {
    state.orderTimer -= dt;
    if (state.orderTimer <= 0) {
      state.orderTimer = ORDER_INTERVAL;
      if (state.orders.length < MAX_ORDERS) {
        const order = generateOrder(state, rng);
        state.orders.push(order);
        events.push({ type: 'newOrder', orderId: order.id, customer: order.customer });
      }
    }
  }

  return events;
}

/** Fast-forwards the simulation in small steps (sleeping, offline progress). */
export function advance(state: GameState, seconds: number, opts: TickOptions): GameEvent[] {
  const events: GameEvent[] = [];
  let remaining = seconds;
  while (remaining > 1e-6) {
    const step = Math.min(remaining, MAX_STEP);
    events.push(...tick(state, step, opts));
    remaining -= step;
  }
  return events;
}

export function isNight(dayTime: number): boolean {
  return dayTime < 0.23 || dayTime > 0.78;
}

/** "7:30 AM" style clock for a day fraction. */
export function formatClock(dayTime: number): string {
  const totalMinutes = Math.floor(dayTime * 24 * 60);
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = Math.floor((totalMinutes % 60) / 10) * 10;
  const suffix = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${suffix}`;
}
